import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getAccessLockGroups,
  getBuilderAccessLock,
  updateBuilderAccessLock
} from '../../services/api/implementation/impl';
import Button from '../../../shared/components/ui/Button';
import DateFormat from '../../utils/DateFormat';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Full access', hint: 'Everything works as normal.' },
  {
    value: 'limited',
    label: 'Limit features',
    hint: 'Only the features ticked below are switched off.'
  },
  {
    value: 'locked',
    label: 'Lock account',
    hint: 'Every user of this builder sees a lock screen until access is restored.'
  }
];

export default function EditBuilderAccessLock({ builder, onClose, onSuccess }) {
  const builderId = builder?.builder_id;
  const [status, setStatus] = useState('active');
  const [lockedGroups, setLockedGroups] = useState([]);
  const [message, setMessage] = useState('');

  const { data: groups = [] } = useQuery({
    queryKey: ['accessLockGroups'],
    queryFn: getAccessLockGroups,
    staleTime: Infinity
  });

  const { data: current, isLoading } = useQuery({
    queryKey: ['builderAccessLock', builderId],
    queryFn: () => getBuilderAccessLock(builderId),
    enabled: !!builderId
  });

  useEffect(() => {
    if (!current) return;
    setStatus(current.status || 'active');
    setLockedGroups(current.locked_groups || []);
    setMessage(current.message || '');
  }, [current]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateBuilderAccessLock(builderId, {
        status,
        locked_groups: status === 'limited' ? lockedGroups : [],
        message: status === 'active' ? null : message.trim() || null
      }),
    onSuccess: () => {
      toast.success(
        status === 'active'
          ? 'Access restored'
          : status === 'locked'
            ? 'Account locked'
            : 'Features limited'
      );
      onSuccess?.();
      onClose?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Save failed');
    }
  });

  const toggleGroup = (key) => {
    setLockedGroups((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const canSave = status !== 'limited' || lockedGroups.length > 0;

  return (
    <div className="space-y-4 p-1">
      <div>
        <p className="text-sm font-semibold text-gray-900">{builder?.builder_name}</p>
        <p className="text-xs text-gray-500">Builder ID {builderId}</p>
        {current?.locked_at && current?.status !== 'active' ? (
          <p className="mt-1 text-xs text-gray-500">
            Restricted since {DateFormat(current.locked_at)}
          </p>
        ) : null}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          <div className="space-y-2">
            {STATUS_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 ${
                  status === option.value ? 'border-[#1E4A53] bg-teal-50/40' : 'border-gray-200'
                }`}>
                <input
                  type="radio"
                  name="access-status"
                  className="mt-1"
                  checked={status === option.value}
                  onChange={() => setStatus(option.value)}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                  <span className="block text-xs text-gray-500">{option.hint}</span>
                </span>
              </label>
            ))}
          </div>

          {status === 'limited' ? (
            <div>
              <p className="mb-1 text-xs font-semibold text-gray-600">Features to switch off</p>
              <div className="grid grid-cols-2 gap-2">
                {groups.map((group) => (
                  <label key={group.key} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={lockedGroups.includes(group.key)}
                      onChange={() => toggleGroup(group.key)}
                    />
                    {group.label}
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          {status !== 'active' ? (
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-600">
                Message shown to the builder (optional)
              </label>
              <textarea
                rows={2}
                maxLength={500}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                placeholder="e.g. Invoice INV-104 of ₹50,000 is overdue since 1 Sep."
              />
            </div>
          ) : null}
        </>
      )}

      <div className="flex flex-wrap gap-2 pt-2">
        <Button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || isLoading || !canSave}>
          Save
        </Button>
        <Button displayType="secondary" type="button" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
